import isEqual from 'lodash/isEqual';

/**
 * GenPT schedules API — client contract (portal `/genpt/schedules`, axios `baseURL`).
 *
 * - Auth / tenant: `Authorization` + `company_id` via `src/index.js` defaults (not duplicated in bodies unless needed).
 * - Persisted fields follow backend rules: `test_config` and `throttling_config` are objects; schedule fields use
 *   snake_case in wire JSON (`pattern_details`, `ends_type`, `repeat_unit`, `freezing_periods`, …).
 * - Enums: `ends_type` ∈ `never` | `on_date` | `after_occurrences`; `repeat_unit` ∈ `day` | `week` | `month` | `year` (lowercase).
 * - Optional UI round-trip: `pattern_details.genpt_recurrence_ui` — small string, no scheduler semantics.
 *   `week:matches` | `week:specific` | `month:specific_date` | `month:matches_date` | `month:relative_day` |
 *   `month:matches_relative`. Persist on GET/PUT/POST with `pattern_details`; backend may store as opaque text.
 * - Create (POST): use {@link buildGenptScheduleCreateBody}; update (PUT): {@link buildGenptScheduleUpdatePutBody} +
 *   {@link pickChangedSchedulePutFields} against a GET snapshot (both include `recurrence_type`). Pause: PATCH `.../status` with `{ status }`.
 * - One-liner for tickets: schedules API persists documented fields only; keep test settings under `test_config`,
 *   use snake_case schedule keys and lowercase enums, partial PUT with diffs from GET `/schedules/:id`, PATCH for pause/resume.
 *
 * {@link buildGenptScheduleApiPayload} is the canonical **internal** camelCase object (forms, Redux, comparisons).
 * HTTP bodies for create/update use the snake_case builders below.
 * Schedule modal collection (URL vs file) for forms: {@link normalizeScheduleModalCollectionBlob}.
 */

const DAYS_SHORT = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

/** Lowercase English weekday → API abbrev; aligns with ScheduleTest `resolveRelativeDay` long names. */
const WEEKDAY_LONG_TO_SHORT = {
  sunday: 'SUN',
  monday: 'MON',
  tuesday: 'TUE',
  wednesday: 'WED',
  thursday: 'THU',
  friday: 'FRI',
  saturday: 'SAT',
};

const GENPT_MONTH_UI_MODES = new Set([
  'specific_date',
  'matches_date',
  'relative_day',
  'matches_relative',
]);

/**
 * Wire value for `pattern_details.genpt_recurrence_ui`, or null when not applicable.
 * @param {object} scheduleUi
 * @param {'once'|'custom'} recurrenceType
 */
export function buildGenptRecurrenceUiHint(scheduleUi, recurrenceType) {
  if (recurrenceType !== 'custom' || !scheduleUi || typeof scheduleUi !== 'object') return null;
  const ru = String(scheduleUi.repeatUnit || '').toLowerCase();
  if (ru === 'week') {
    const wp = scheduleUi.weeklyPattern;
    if (wp === 'matches' || wp === 'specific') return `week:${wp}`;
    return null;
  }
  if (ru === 'month') {
    const mp = String(scheduleUi.monthlyPattern || '').trim();
    if (GENPT_MONTH_UI_MODES.has(mp)) return `month:${mp}`;
    return null;
  }
  return null;
}

/**
 * @param {unknown} raw — e.g. `"week:matches"` from GET `pattern_details`
 * @returns {{ unit: 'week'|'month', mode: string }|null}
 */
export function parseGenptRecurrenceUiHint(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  const s = String(raw).trim().toLowerCase();
  const idx = s.indexOf(':');
  if (idx < 1) return null;
  const unit = s.slice(0, idx);
  const mode = s.slice(idx + 1);
  if (!mode) return null;
  if (unit === 'week' && (mode === 'matches' || mode === 'specific')) return { unit: 'week', mode };
  if (unit === 'month' && GENPT_MONTH_UI_MODES.has(mode)) return { unit: 'month', mode };
  return null;
}

function to24HourTime(hour12, minute, ampm) {
  let h = parseInt(String(hour12), 10);
  const m = String(minute || '00').padStart(2, '0');
  if (Number.isNaN(h)) return '09:00:00';
  const ap = String(ampm || 'AM').toUpperCase();
  if (ap === 'PM' && h !== 12) h += 12;
  if (ap === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${m}:00`;
}

function parseDayOfMonthFromLabel(label) {
  const m = /^(\d+)/.exec(String(label || ''));
  return m ? parseInt(m[1], 10) : null;
}

function mapWeekRank(position) {
  const p = String(position || '');
  const map = {
    First: 'first',
    Second: 'second',
    Third: 'third',
    Fourth: 'fourth',
    /** Backend supports first|second|third|fourth|last only; coerce legacy "Fifth" → "last". */
    Fifth: 'last',
    Last: 'last',
  };
  return map[p] || (p ? p.toLowerCase() : null);
}

/**
 * UI relative-day pick → backend `pattern_details` shape.
 *
 * Contract (from GenPT schedules API doc):
 *  - Day     → `day_of_month: 1` (First) or `-1` (Last); `week_rank` not used.
 *  - Weekday → `week_rank` + `is_weekday_only: true` (Mon–Fri).
 *  - Weekend → `week_rank` + `is_weekend_only: true`.
 *  - Nth calendar weekday (e.g. Fourth Tuesday) → `week_rank` + `days_of_week: ['TUE']` (matches UI
 *    `matches_relative` / `resolveRelativeDay` long names like `Tuesday`).
 */
function mapMonthlyRelativeToPattern(position, relativeDay) {
  const wr = mapWeekRank(position);
  const d = String(relativeDay || '');
  if (d === 'Day') {
    return { day_of_month: wr === 'last' ? -1 : 1 };
  }
  if (d === 'Weekday') {
    return { week_rank: wr, is_weekday_only: true };
  }
  if (d === 'Weekend') {
    return { week_rank: wr, is_weekend_only: true };
  }
  const shortFromLong = WEEKDAY_LONG_TO_SHORT[d.trim().toLowerCase()];
  if (shortFromLong && wr) {
    return { week_rank: wr, days_of_week: [shortFromLong] };
  }
  return { week_rank: wr };
}

/** Same “calendar week row” rule as ScheduleTest `ordinal` (used for monthly `matches_relative`). */
export function getOrdinalFromStartDate(startDate) {
  const d = new Date(`${startDate}T12:00:00`);
  if (isNaN(d.getTime())) return 'First';
  const dom = d.getDate();
  const ordinals = ['First', 'Second', 'Third', 'Fourth', 'Fifth'];
  const ordIdx = Math.ceil(dom / 7) - 1;
  return ordinals[ordIdx] || 'Last';
}

/**
 * Build patternDetails for week/month/year custom repeats.
 */
export function buildPatternDetails(scheduleUi) {
  const {
    recurrencyType,
    repeatUnit,
    weeklyPattern,
    selectedDays,
    monthlyPattern,
    monthlySpecificDate,
    monthlyRelativePosition,
    monthlyRelativeDay,
    startDate,
  } = scheduleUi;

  /** Wire shape per GenPT schedules API: `days_of_week`, `day_of_month`, `week_rank`,
 * `is_weekday_only`, `is_weekend_only`, `month_of_year`. */
  const base = {
    days_of_week: [],
    day_of_month: null,
    week_rank: null,
    is_weekday_only: false,
    month_of_year: null,
  };

  if (recurrencyType === 'once') {
    return { ...base };
  }

  if (repeatUnit === 'Week') {
    if (weeklyPattern === 'specific' && Array.isArray(selectedDays) && selectedDays.length > 0) {
      return { ...base, days_of_week: [...selectedDays] };
    }
    const d = new Date(`${startDate}T12:00:00`);
    if (!isNaN(d.getTime())) {
      return { ...base, days_of_week: [DAYS_SHORT[d.getDay()]] };
    }
    return { ...base };
  }

  if (repeatUnit === 'Month') {
    if (monthlyPattern === 'specific_date' || monthlyPattern === 'matches_date') {
      const dom =
        monthlyPattern === 'specific_date'
          ? parseDayOfMonthFromLabel(monthlySpecificDate)
          : new Date(`${startDate}T12:00:00`).getDate();
      return { ...base, day_of_month: dom };
    }
    if (monthlyPattern === 'relative_day' || monthlyPattern === 'matches_relative') {
      const position =
        monthlyPattern === 'relative_day'
          ? monthlyRelativePosition
          : getOrdinalFromStartDate(startDate);
      let relativeDayForPattern = monthlyRelativeDay;
      if (monthlyPattern === 'matches_relative') {
        const anchor = new Date(`${startDate}T12:00:00`);
        if (!isNaN(anchor.getTime())) {
          relativeDayForPattern = anchor.toLocaleString('en-US', { weekday: 'long' });
        }
      }
      return { ...base, ...mapMonthlyRelativeToPattern(position, relativeDayForPattern) };
    }
  }

  if (repeatUnit === 'Year') {
    const d = new Date(`${startDate}T12:00:00`);
    if (!isNaN(d.getTime())) {
      /** Yearly requires both `month_of_year` and `day_of_month` (per API doc). */
      return { ...base, month_of_year: d.getMonth() + 1, day_of_month: d.getDate() };
    }
  }

  return { ...base };
}

function mapEndsType(uiEnds) {
  if (uiEnds === 'on') return 'on_date';
  if (uiEnds === 'after') return 'after_occurrences';
  return 'never';
}

function mapRepeatUnit(uiUnit) {
  const u = String(uiUnit || 'day').toLowerCase();
  if (['day', 'week', 'month', 'year'].includes(u)) return u;
  return String(uiUnit || 'Day').toLowerCase();
}

function buildFreezingPeriods(freezingPeriods) {
  if (!Array.isArray(freezingPeriods) || freezingPeriods.length === 0) return [];

  return freezingPeriods
    .map((p) => {
      if (!p?.startDate || !p?.endDate) return null;
      const startT = to24HourTime(
        (p.startTime || '09:00').split(':')[0],
        (p.startTime || '09:00').split(':')[1] || '00',
        p.startAmPm || 'AM'
      );
      const endT = to24HourTime(
        (p.endTime || '05:00').split(':')[0],
        (p.endTime || '05:00').split(':')[1] || '00',
        p.endAmPm || 'PM'
      );
      const start = new Date(`${p.startDate}T${startT}`);
      const end = new Date(`${p.endDate}T${endT}`);
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
      return { startAt: start.toISOString(), endAt: end.toISOString() };
    })
    .filter(Boolean);
}

/**
 * Valid freezing windows as millisecond ranges — same wall parsing as {@link buildFreezingPeriods}.
 * Used by ScheduleTest execution preview (ROAR-4385).
 */
export function getFreezingWallMsRangesFromUi(freezingPeriods) {
  return buildFreezingPeriods(freezingPeriods)
    .map(({ startAt, endAt }) => {
      const startMs = new Date(startAt).getTime();
      const endMs = new Date(endAt).getTime();
      if (Number.isNaN(startMs) || Number.isNaN(endMs)) return null;
      const lo = Math.min(startMs, endMs);
      const hi = Math.max(startMs, endMs);
      return { start: lo, end: hi };
    })
    .filter((r) => r != null && r.end >= r.start);
}

const THROTTLE_STRATEGY_VALUES = new Set(['stealth', 'standard', 'turbo', 'custom']);

/** Same numeric triples as `ThrottlingSetup` / `formatGenptSessionTestSummary` — infer preset when `strategy` is absent. */
const THROTTLING_PRESET_NUMBERS = {
  stealth: { rate_limit: 10, concurrency: 1, timeout: 10 },
  standard: { rate_limit: 80, concurrency: 10, timeout: 5 },
  turbo: { rate_limit: 120, concurrency: 20, timeout: 3 },
};

/**
 * Normalize throttle preset for `throttling_config.strategy` and `config_settings.strategy`.
 * Prefers explicit `strategy`; otherwise infers from vulnerability numbers (legacy schedule rows).
 */
export function resolveThrottleStrategyForWire({ strategy, vulnerability } = {}) {
  const raw = String(strategy ?? '').trim().toLowerCase();
  if (THROTTLE_STRATEGY_VALUES.has(raw)) return raw;
  const v = vulnerability && typeof vulnerability === 'object' ? vulnerability : null;
  if (!v) return 'standard';
  const rl = v.rate_limit ?? v.rateLimit;
  const c = v.concurrency;
  const t = v.timeout ?? v.requestTimeout ?? v.request_timeout;
  for (const [name, preset] of Object.entries(THROTTLING_PRESET_NUMBERS)) {
    if (rl === preset.rate_limit && c === preset.concurrency && t === preset.timeout) return name;
  }
  return 'custom';
}

export function buildThrottlingConfigFromSettings(configSettings) {
  const v = configSettings?.vulnerability;
  if (!v) {
    const strategy = resolveThrottleStrategyForWire({
      strategy: configSettings?.strategy,
      vulnerability: null,
    });
    return {
      maxConcurrent: 10,
      rateLimit: 80,
      requestTimeout: 5,
      strategy,
    };
  }
  const strategy = resolveThrottleStrategyForWire({
    strategy: configSettings?.strategy,
    vulnerability: v,
  });
  return {
    maxConcurrent: v.concurrency,
    rateLimit: v.rate_limit,
    requestTimeout: v.timeout,
    retries: v.retries,
    validation: configSettings?.validation || undefined,
    strategy,
  };
}

/**
 * Minimal test_config for schedule row (product-defined JSON).
 * When the user uses only URLs or only files, the other modality is cleared explicitly so PUT does not
 * leave stale sibling keys merged on the server. For schedule **updates**, use `opts.clearSiblingAsNull`
 * to send `null` (explicit unset); for **create** and baselines, omit or use `[]` via the default.
 */
export function buildTestConfigForSchedule(testConfigData, opts = {}) {
  if (!testConfigData) return {};
  const clearSibling =
    opts.clearSiblingAsNull === true ? null : [];
  const {
    testType,
    authType,
    continueAsUnauth,
    networkType,
    targetURL,
    assetId,
    assetType: assetTypeCamel,
    asset_type: assetTypeSnake,
    selectedVpnProfile,
    loginUrl,
    savedCredential,
    cookieAuthentication,
    customHeaders,
    api_collection_urls: apiCollectionUrls,
    api_files: apiFiles,
    schema_endpoint_url: schemaEndpointUrl,
    username,
    password,
    credential_name,
    credentialName,
    mfaSecretKey,
    mfa_secret_key,
    savedCredentialName,
    crmType,
    crm_type,
  } = testConfigData;

  const assetTypeRaw = assetTypeCamel ?? assetTypeSnake ?? null;
  const assetTypeNorm =
    assetTypeRaw != null && String(assetTypeRaw).trim() !== ''
      ? String(assetTypeRaw).trim().toLowerCase()
      : null;

  const savedCredentialId =
    savedCredential != null
      ? typeof savedCredential === 'object'
        ? savedCredential?.credential_id ?? savedCredential?.id ?? savedCredential
        : savedCredential
      : null;

  const normalizedUrls = Array.isArray(apiCollectionUrls)
    ? apiCollectionUrls.map((u) => String(u).trim()).filter(Boolean)
    : [];
  const normalizedFiles = Array.isArray(apiFiles)
    ? apiFiles
        .map((f) => {
          const file_key = f?.file_key ?? f?.fileKey;
          if (!file_key) return null;
          const file_name = f?.file_name ?? f?.fileName;
          return file_name ? { file_key, file_name } : { file_key };
        })
        .filter(Boolean)
    : [];
  const hasUrls = normalizedUrls.length > 0;
  const hasFiles = normalizedFiles.length > 0;

  const testTypeNorm = testType != null ? String(testType).trim().toLowerCase() : '';
  const userStr = typeof username === 'string' ? username.trim() : '';
  const passStr = typeof password === 'string' ? password.trim() : '';
  const credNameRaw = credential_name ?? credentialName;
  const credNameStr = typeof credNameRaw === 'string' ? credNameRaw.trim() : '';
  const mfaRaw = mfaSecretKey ?? mfa_secret_key;
  const mfaStr =
    mfaRaw != null && String(mfaRaw).trim() !== ''
      ? String(mfaRaw).replace(/\s+/g, '')
      : '';
  const savedCredNameStr =
    typeof savedCredentialName === 'string' ? savedCredentialName.trim() : '';

  const crmForWordPress =
    testTypeNorm === 'wordpress'
      ? (() => {
          const c = crmType ?? crm_type;
          if (c != null && String(c).trim() !== '') return String(c).trim().toLowerCase();
          return 'wordpress';
        })()
      : null;

  return {
    ...(testType != null && { testType }),
    ...(authType != null && { authType }),
    ...(continueAsUnauth !== undefined && { continueAsUnauth }),
    ...(networkType != null && { networkType }),
    ...(targetURL != null && { targetURL }),
    ...(assetId != null && { assetId }),
    ...(assetTypeNorm != null && { assetType: assetTypeNorm }),
    ...(selectedVpnProfile != null && {
      vpnProfileId:
        typeof selectedVpnProfile === 'object'
          ? selectedVpnProfile.id ?? selectedVpnProfile
          : selectedVpnProfile,
    }),
    ...(loginUrl != null && { loginUrl }),
    /** Wire: `saved_credential` (id), not `saved_credential_id` — `toSnakeDeep` leaves snake_case keys as-is. */
    ...(savedCredentialId != null && { saved_credential: savedCredentialId }),
    ...(cookieAuthentication && { cookieAuthentication }),
    ...(Array.isArray(customHeaders) &&
      customHeaders.length > 0 && { customHeaders }),
    ...(hasUrls
      ? { api_collection_urls: normalizedUrls }
      : hasFiles
        ? { api_collection_urls: clearSibling }
        : {}),
    ...(hasFiles
      ? { api_files: normalizedFiles }
      : hasUrls
        ? { api_files: clearSibling }
        : {}),
    ...(schemaEndpointUrl != null &&
      String(schemaEndpointUrl).trim() !== '' && {
        schema_endpoint_url: String(schemaEndpointUrl).trim(),
      }),
    ...(crmForWordPress != null && { crmType: crmForWordPress }),
    ...(userStr !== '' && { username: userStr }),
    ...(passStr !== '' && { password: passStr }),
    ...(credNameStr !== '' && { credential_name: credNameStr }),
    ...(mfaStr !== '' && { mfaSecretKey: mfaStr }),
    ...(savedCredentialId != null &&
      savedCredNameStr !== '' && { savedCredentialName: savedCredNameStr }),
  };
}

/**
 * Schedule modal: one normalized blob from GET prefill or live widget (`api_collection_urls` / `api_files` / `schema_endpoint_url`).
 * `null` if none. To merge onto `submittedData` without stale siblings: `delete` those three keys on the target, then
 * `Object.assign(target, normalizeScheduleModalCollectionBlob(widget) || {})` when the widget object exists.
 */
export function normalizeScheduleModalCollectionBlob(input) {
  if (!input || typeof input !== 'object') return null;
  const urls = input.api_collection_urls;
  const files = input.api_files;
  const schema = input.schema_endpoint_url;
  const hasUrls =
    Array.isArray(urls) && urls.map((u) => String(u).trim()).filter(Boolean).length > 0;
  const hasFiles =
    Array.isArray(files) &&
    files.length > 0 &&
    files.some((f) => f && (f.file_key || f.fileKey));
  const hasSchema = schema != null && String(schema).trim() !== '';
  if (!hasUrls && !hasFiles && !hasSchema) return null;
  const out = {};
  if (hasUrls) {
    out.api_collection_urls = urls.map((u) => String(u).trim()).filter(Boolean);
  }
  if (hasFiles) {
    out.api_files = files
      .map((f) => {
        const file_key = f?.file_key ?? f?.fileKey;
        if (!file_key) return null;
        const file_name = f?.file_name ?? f?.fileName;
        return file_name ? { file_key, file_name } : { file_key };
      })
      .filter(Boolean);
  }
  if (hasSchema) out.schema_endpoint_url = String(schema).trim();
  return out;
}

/**
 * Internal schedule object (camelCase) for comparisons and for converting to wire JSON.
 * Also used when nesting `schedule` under other payloads (e.g. start-test) — those callers may keep camelCase
 * if their endpoint expects it; standalone POST `/genpt/schedules` should use {@link buildGenptScheduleCreateBody}.
 * @param {boolean} [forSchedulesEndpointOnly] - When true, omits `status` / `triggerSource` (standalone schedule create).
 * @param {boolean} [clearSiblingCollectionFieldsAsNull] - When true (schedule PUT “current” payload), cleared collection
 *   sibling is sent as JSON `null`; when false (create / diff baselines), as `[]` for APIs that reject null lists.
 */
export function buildGenptScheduleApiPayload({
  scheduleUi,
  testConfigData,
  configSettings,
  assetId,
  companyId,
  forSchedulesEndpointOnly = false,
  clearSiblingCollectionFieldsAsNull = false,
}) {
  const name = (scheduleUi.scheduleName || '').trim() || 'GenPT schedule';
  const testStartTime = to24HourTime(
    scheduleUi.startHour,
    scheduleUi.startMin,
    scheduleUi.startAmPm
  );

  const recurrenceType = scheduleUi.recurrencyType === 'once' ? 'once' : 'custom';

  let repeatUnit = mapRepeatUnit(scheduleUi.repeatUnit);
  let repeatInterval = parseInt(String(scheduleUi.repeatValue || '1'), 10);
  if (Number.isNaN(repeatInterval) || repeatInterval < 0) repeatInterval = 1;

  if (recurrenceType === 'once') {
    repeatUnit = 'day';
    repeatInterval = 0;
  }

  const endsType = mapEndsType(scheduleUi.endsType);
  const patternDetailsBase = buildPatternDetails(scheduleUi);
  const recurrenceUi = buildGenptRecurrenceUiHint(scheduleUi, recurrenceType);
  const patternDetails =
    recurrenceUi != null
      ? { ...patternDetailsBase, genpt_recurrence_ui: recurrenceUi }
      : patternDetailsBase;

  const payload = {
    ...(assetId != null && { assetId }),
    ...(companyId != null && { companyId }),
    name,
    recurrenceType,
    startDate: scheduleUi.startDate,
    testStartTime,
    timezone: scheduleUi.timeZone || 'UTC',
    repeatUnit,
    repeatInterval,
    endsType,
    patternDetails,
    testConfig: buildTestConfigForSchedule(testConfigData, {
      clearSiblingAsNull: clearSiblingCollectionFieldsAsNull,
    }),
    throttlingConfig: buildThrottlingConfigFromSettings(configSettings),
    ...(!forSchedulesEndpointOnly && {
      status: 'active',
      triggerSource: 'ui',
    }),
    freezingPeriods: scheduleUi.isFreezingEnabled
      ? buildFreezingPeriods(scheduleUi.freezingPeriods)
      : [],
  };

  if (endsType === 'on_date') {
    payload.endsOnDate = scheduleUi.endsOnDate;
  }
  if (endsType === 'after_occurrences') {
    const n = parseInt(String(scheduleUi.endsAfterCount || '0'), 10);
    const safe = Number.isNaN(n) ? 0 : Math.max(0, n);
    /** ROAR-4323: UI allows max 999; clamp wire payload */
    payload.endsAfterCount = Math.min(999, safe);
  }

  return payload;
}

/** DB / API contract: lowercase only (never | on_date | after_occurrences). */
function canonicalEndsTypeForPut(endsType) {
  const t = String(endsType ?? 'never').trim().toLowerCase();
  if (t === 'on_date') return 'on_date';
  if (t === 'after_occurrences') return 'after_occurrences';
  return 'never';
}

const GENPT_REPEAT_UNITS = ['day', 'week', 'month', 'year'];

function canonicalRepeatUnitForPut(unit) {
  const u = String(unit || 'day').trim().toLowerCase();
  return GENPT_REPEAT_UNITS.includes(u) ? u : 'day';
}

function toSnakeKey(k) {
  return String(k).replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();
}

function toSnakeDeep(val) {
  if (val === null || val === undefined) return val;
  if (Array.isArray(val)) return val.map(toSnakeDeep);
  if (typeof val === 'object' && Object.getPrototypeOf(val) === Object.prototype) {
    const out = {};
    for (const [k, v] of Object.entries(val)) {
      if (v === undefined) continue;
      out[toSnakeKey(k)] = toSnakeDeep(v);
    }
    return out;
  }
  return val;
}

function mapFreezingPeriodsToPut(freezingPeriods) {
  if (!Array.isArray(freezingPeriods)) return [];
  return freezingPeriods
    .map((p) => {
      const startAt = p.startAt ?? p.start_at;
      const endAt = p.endAt ?? p.end_at;
      if (!startAt || !endAt) return null;
      return { start_at: startAt, end_at: endAt };
    })
    .filter(Boolean);
}

/**
 * Body for PUT /genpt/schedules/:id (snake_case).
 * `ends_type` and `repeat_unit` use canonical lowercase DB values; not display enums (NEVER, etc.).
 * @param {ReturnType<typeof buildGenptScheduleApiPayload>} internal - Output of {@link buildGenptScheduleApiPayload}
 */
export function buildGenptScheduleUpdatePutBody(internal) {
  const endsType = canonicalEndsTypeForPut(internal.endsType);
  const body = {
    name: internal.name,
    /** ROAR-4359: PUT must persist `once` vs `custom`; omitting it left edits on “Run only once” as recurring/daily on the server. */
    recurrence_type: internal.recurrenceType === 'once' ? 'once' : 'custom',
    start_date: internal.startDate,
    test_start_time: internal.testStartTime,
    timezone: internal.timezone,
    repeat_unit: canonicalRepeatUnitForPut(internal.repeatUnit),
    repeat_interval: internal.repeatInterval,
    pattern_details: internal.patternDetails,
    ends_type: endsType,
    test_config: toSnakeDeep(internal.testConfig || {}) || {},
    throttling_config: toSnakeDeep(internal.throttlingConfig || {}) || {},
    freezing_periods: mapFreezingPeriodsToPut(internal.freezingPeriods),
  };
  if (endsType === 'on_date' && internal.endsOnDate) {
    body.ends_on_date = internal.endsOnDate;
  }
  if (endsType === 'after_occurrences') {
    const n = internal.endsAfterCount;
    const raw = n != null && !Number.isNaN(Number(n)) ? Math.max(0, Number(n)) : 0;
    body.ends_after_count = Math.min(999, raw);
  }
  return body;
}

/**
 * Full snake_case body for POST `/genpt/schedules` — same field names and nesting as PUT, plus create-only keys.
 * Keeps `test_config` / `throttling_config` as non-null objects per API contract.
 *
 * @param {ReturnType<typeof buildGenptScheduleApiPayload>} internal - Output of {@link buildGenptScheduleApiPayload}
 * @returns {Record<string, unknown>}
 */
export function buildGenptScheduleCreateBody(internal) {
  const body = buildGenptScheduleUpdatePutBody(internal);
  const out = {
    ...body,
  };
  if (internal.assetId != null && internal.assetId !== '') {
    out.assetId = internal.assetId;
  }
  if (internal.companyId != null && internal.companyId !== '') {
    out.company_id = internal.companyId;
  }
  return out;
}

/**
 * Recurrence-related keys persisted together on PUT `/genpt/schedules/:id`.
 * Conditional keys (`ends_on_date`, `ends_after_count`) only appear on `nextFull` when
 * `ends_type` selects them; see {@link buildGenptScheduleUpdatePutBody}.
 */
const SCHEDULE_RECURRENCE_PUT_KEYS = [
  'recurrence_type',
  'repeat_unit',
  'repeat_interval',
  'pattern_details',
  'ends_type',
  'ends_on_date',
  'ends_after_count',
];

/**
 * Partial PUT: include only fields that differ from the snapshot built from GET (e.g. omits unchanged `start_date`).
 * When all freezing windows are removed, ensures `freezing_periods: []` is sent if the server previously had periods.
 *
 * ROAR-4424: when ANY recurrence-related field differs, the **full** recurrence spec is sent
 * (every key from {@link SCHEDULE_RECURRENCE_PUT_KEYS} present on `nextFull`). Backend applies
 * partial PUTs by merging unsent fields from the previous row, and ONCE rows persist forced
 * overrides (`repeat_unit=null`, `ends_type='after_occurrences'`, `ends_after_count=1`). Sending
 * only the changed subset (e.g. `recurrence_type` + `repeat_interval`) on a ONCE → Custom edit
 * gets rejected with HTTP 400 "repeat_unit is required" or silently inherits ONCE end-after-1.
 *
 * @param {Record<string, unknown>} nextFull - {@link buildGenptScheduleUpdatePutBody}(current form)
 * @param {Record<string, unknown>|null|undefined} previousFull - same builder applied to GET-derived baseline
 */
export function pickChangedSchedulePutFields(nextFull, previousFull) {
  if (!previousFull || typeof previousFull !== 'object') {
    return { ...nextFull };
  }
  const out = {};
  for (const key of Object.keys(nextFull)) {
    if (!isEqual(nextFull[key], previousFull[key])) {
      out[key] = nextFull[key];
    }
  }

  const anyRecurrenceChanged = SCHEDULE_RECURRENCE_PUT_KEYS.some(
    (k) => !isEqual(nextFull[k], previousFull[k])
  );
  if (anyRecurrenceChanged) {
    for (const k of SCHEDULE_RECURRENCE_PUT_KEYS) {
      if (Object.prototype.hasOwnProperty.call(nextFull, k)) {
        out[k] = nextFull[k];
      } else {
        /** Conditional key dropped (e.g. switching `after_occurrences` / `on_date` → `never`):
         * leave it off the PUT body; backend nulls the sibling field per the new `ends_type`. */
        delete out[k];
      }
    }
  }

  const prevFp = previousFull.freezing_periods;
  const nextFp = nextFull.freezing_periods;
  if (
    Array.isArray(prevFp) &&
    prevFp.length > 0 &&
    Array.isArray(nextFp) &&
    nextFp.length === 0 &&
    out.freezing_periods === undefined
  ) {
    out.freezing_periods = [];
  }
  return out;
}

export default buildGenptScheduleApiPayload;
